import React from "react";
import PropTypes from "prop-types";
import { Typography, Autocomplete, TextField } from "@mui/material";
import { Icon } from "@iconify/react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import organisationType from "../../../data/organisationType.json";
import ImageUploader from "../../../components/Reusables/ImageControls/ImageUploader";
import MultiImageUploader from "../../../components/Reusables/ImageControls/MultiImageUploader";
import StepNavigationButtons from "../../../components/Reusables/StepControls/StepNavigationButtons";
import CountryCodePicker from "../../../components/CountryCodePicker";
import { sanitizePhoneNumber } from "../../../utils/phoneNumber";
import FieldLabel from "./FieldLabel";
import { AUTOCOMPLETE_MENU_SX } from "../onboardingStyles";

const AddCenterStep = ({
  centerLogoFile,
  setCenterLogoFile,
  centerLogoPreview,
  setCenterLogoPreview,
  center,
  setCenter,
  errors,
  centerPhoneLimit,
  onOpenAddressSearch,
  orgImages,
  setOrgImages,
  onBack,
  onProceed,
  loading,
}) => (
  <div>
    <br />
    <Typography
      variant="h6"
      className="step-title"
      sx={{ fontFamily: "Figtree", fontWeight: 600 }}
    >
      Add Centre Details
    </Typography>
    <br />
    <div className="row">
      <div className="col-12">
        <div className="form-group">
          <div
            style={{
              position: "relative",
              width: 120,
              height: 120,
              marginBottom: 30,
            }}
          >
            <ImageUploader
              label="Upload Logo"
              file={centerLogoFile}
              setFile={setCenterLogoFile}
              preview={centerLogoPreview}
              setPreview={setCenterLogoPreview}
            />
          </div>
        </div>
      </div>

      <div className="col-12">
        <div className="form-group">
          <FieldLabel required>Centre Name</FieldLabel>
          <input
            type="text"
            value={center?.centerName}
            onChange={(e) =>
              setCenter((prev) => ({
                ...prev,
                centerName: e.target.value,
              }))
            }
            className="form-control"
            placeholder="Enter Centre Name"
          />
          {errors.centerName && (
            <span className="text-danger">{errors.centerName}</span>
          )}
        </div>
      </div>

      <div className="col-12">
        <div className="form-group">
          <FieldLabel required>Centre Type</FieldLabel>

          <Autocomplete
            options={organisationType}
            value={center?.centerType || null}
            onChange={(_, value) =>
              setCenter((prev) => ({
                ...prev,
                centerType: value,
              }))
            }
            clearOnEscape
            disablePortal
            popupIcon={<KeyboardArrowDownIcon />}
            ListboxProps={{
              style: {
                maxHeight: "250px",
                overflowY: "auto",
              },
            }}
            sx={AUTOCOMPLETE_MENU_SX}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Centre Type"
                error={false}
                sx={{
                  backgroundColor: "var(--app-surface)",
                  borderRadius: "8px",

                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    height: "48px",
                    backgroundColor: "var(--app-surface)",

                    "& fieldset": {
                      border: "1px solid var(--app-border) !important",
                    },

                    "&:hover fieldset": {
                      border: "1px solid var(--app-border) !important",
                    },

                    "&.Mui-focused fieldset": {
                      border: "1px solid var(--app-border) !important",
                    },

                    "&.Mui-focused": {
                      boxShadow: "none !important",
                      outline: "none !important",
                    },
                  },

                  "& .MuiInputBase-input": {
                    padding: "12px 14px",
                    fontSize: "14px",
                    fontWeight: 400,
                    color: "var(--app-text)",
                  },

                  "& .MuiInputBase-input::placeholder": {
                    color: "var(--app-text)",
                    opacity: 1,
                  },
                }}
              />
            )}
          />

          {errors.centerType && (
            <Typography variant="caption" color="error">
              {errors.centerType}
            </Typography>
          )}
        </div>
      </div>

      <div className="col-12">
        <div className="form-group">
          <FieldLabel>Centre Contact Number</FieldLabel>

          <div className="phone-field-row">
            <div className="phone-field-code">
              <CountryCodePicker
                value={center?.countryCode || "+91"}
                onChange={(nextCountryCode) =>
                  setCenter((prev) => ({
                    ...prev,
                    countryCode: nextCountryCode,
                    centerPhoneNumber: sanitizePhoneNumber(
                      prev.centerPhoneNumber,
                      nextCountryCode,
                    ),
                  }))
                }
              />
            </div>
            <div className="phone-field-number">
              <input
                type="text"
                value={center?.centerPhoneNumber || ""}
                maxLength={centerPhoneLimit}
                inputMode="numeric"
                onChange={(e) => {
                  setCenter((prev) => ({
                    ...prev,
                    centerPhoneNumber: sanitizePhoneNumber(
                      e.target.value,
                      prev.countryCode || "+91",
                    ),
                  }));
                }}
                className="form-control"
                placeholder="Enter Contact Number"
              />
            </div>
          </div>

          {errors.centerPhoneNumber && (
            <span className="text-danger">{errors.centerPhoneNumber}</span>
          )}
        </div>
      </div>

      <div className="col-12">
        <div className="form-group">
          <FieldLabel required>Centre Address</FieldLabel>

          <div style={{ position: "relative", width: "100%" }}>
            <input
              type="text"
              className="form-control"
              onClick={onOpenAddressSearch}
              value={center?.centerAddress}
              placeholder="Add Address"
              readOnly
              style={{ paddingRight: "35px" }} // space for the icon
            />
            <Icon
              icon="mdi:map-marker"
              width="22"
              height="22"
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--app-text-muted)",
                pointerEvents: "none", // allow click to pass through
              }}
            />
          </div>

          {errors.centerAddress && (
            <small className="text-danger">{errors.centerAddress}</small>
          )}
        </div>
      </div>

      <div className="col-12">
        <div style={{ display: "flex", flexDirection: "column" }}>
          <MultiImageUploader
            label="Centre Images (upto 4)"
            images={orgImages}
            setImages={setOrgImages}
          />
        </div>
      </div>

      <StepNavigationButtons
        onBack={onBack}
        onProceed={onProceed}
        loading={loading}
      />
    </div>
  </div>
);

AddCenterStep.propTypes = {
  centerLogoFile: PropTypes.object,
  setCenterLogoFile: PropTypes.func.isRequired,
  centerLogoPreview: PropTypes.string,
  setCenterLogoPreview: PropTypes.func.isRequired,
  center: PropTypes.object.isRequired,
  setCenter: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  centerPhoneLimit: PropTypes.number,
  onOpenAddressSearch: PropTypes.func.isRequired,
  orgImages: PropTypes.array.isRequired,
  setOrgImages: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onProceed: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

export default AddCenterStep;
